import { useState, useEffect, useCallback } from "react";
import { useTheme } from "@/lib/ThemeContext";
import { THEME_PRESETS } from "@/lib/themePresets";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Crown, Check, Star, Zap, Trophy } from "lucide-react";

const RARITY_COLORS = {
  starter: "#A1A1AA",
  common: "#22C55E",
  uncommon: "#3B82F6",
  rare: "#A855F7",
  epic: "#F59E0B",
  legendary: "#EF4444",
};

const RARITY_LABELS = {
  starter: "Starter",
  common: "Common",
  uncommon: "Uncommon",
  rare: "Rare",
  epic: "Epic",
  legendary: "Legendary",
};

function ThemePreviewCard({ theme, selected, onSelect, rarity }) {
  const preset = THEME_PRESETS.find((t) => t.id === theme.id);
  const preview = preset?.preview || {};
  const tokens = preset?.tokens || {};
  const color = RARITY_COLORS[rarity] || "#A1A1AA";

  return (
    <button
      className="w-full rounded-2xl overflow-hidden transition-all hover:scale-[1.02] relative"
      style={{
        border: selected ? `2px solid ${color}` : "2px solid #2A2A3A",
        boxShadow: selected ? `0 0 20px ${color}30` : "none",
      }}
      onClick={onSelect}
      data-testid={`theme-choice-${theme.id}`}>
      {/* Preview area */}
      <div className="h-28 relative" style={{ background: preview.bg || "#111" }}>
        {/* Simulated UI elements */}
        <div className="absolute inset-3 flex flex-col gap-1.5">
          <div className="flex gap-1.5">
            <div className="h-2 rounded-full flex-1" style={{ background: `${preview.accent || "#fff"}30` }} />
            <div className="h-2 w-8 rounded-full" style={{ background: preview.accent || "#fff" }} />
          </div>
          <div className="flex-1 grid grid-cols-2 gap-1.5">
            <div className="rounded-lg" style={{ background: `${preview.accent || "#fff"}10`, border: `1px solid ${preview.accent || "#fff"}20` }} />
            <div className="rounded-lg" style={{ background: `${preview.accent2 || "#888"}10`, border: `1px solid ${preview.accent2 || "#888"}20` }} />
          </div>
          <div className="flex gap-1">
            <div className="h-1.5 rounded-full flex-1" style={{ background: `${preview.accent || "#fff"}40` }} />
            <div className="h-1.5 w-6 rounded-full" style={{ background: `${preview.accent2 || "#888"}40` }} />
          </div>
        </div>
        {/* Accent dots */}
        <div className="absolute bottom-2 right-2 flex gap-1">
          <div className="w-3 h-3 rounded-full" style={{ background: preview.accent, boxShadow: `0 0 6px ${preview.accent}50` }} />
          <div className="w-3 h-3 rounded-full" style={{ background: preview.accent2 }} />
        </div>
        {selected && (
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ background: color }}>
            <Check className="h-3.5 w-3.5 text-white" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3 space-y-1.5" style={{ background: "#14141E" }}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold" style={{ color: "#F0F0F5" }}>{theme.name}</span>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
            style={{ color, background: `${color}15`, border: `1px solid ${color}30` }}>
            {RARITY_LABELS[rarity] || "Common"}
          </span>
        </div>
        <span className="text-[10px] block capitalize" style={{ color: "#52526B" }}>{theme.cat} theme</span>
      </div>
    </button>
  );
}

export default function LevelUpModal() {
  const { setTheme } = useTheme();
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [visible, setVisible] = useState(false);

  // Bundle L PR-1 — per-session dismissal gate.
  // iter63 reported: modal re-appears on every navigation (/, /settings/*).
  // Root cause: "Choose later" only set local `visible` state; the 30s
  // polling + fresh page loads re-enter `load()` which re-opens the modal
  // as long as the server still reports `pending_choices > 0`. Since
  // "Choose later" is a defer-not-dismiss user action, track it locally
  // per login session so it stays dismissed until next login (or until
  // the user manually re-opens via a future UI).
  //
  // Flag: `mmntm:level-up-dismissed-session`. Value: current `mmntm-device-id`
  // (stable per device, regenerated only on localStorage clear). Flag is
  // cleared on `AuthContext.login()` so next login re-shows the reward.
  const DISMISS_KEY = "mmntm:level-up-dismissed-session";
  const getSessionId = () => {
    try {
      let id = localStorage.getItem("mmntm-device-id");
      if (!id) {
        id = `dev-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        localStorage.setItem("mmntm-device-id", id);
      }
      return id;
    } catch { return "fallback-session"; }
  };
  const isDismissedThisSession = () => {
    try { return localStorage.getItem(DISMISS_KEY) === getSessionId(); }
    catch { return false; }
  };

  const load = useCallback(async () => {
    if (isDismissedThisSession()) { setVisible(false); return; }
    try {
      const res = await api.getThemeChoices();
      if (res.pending_choices > 0 && res.choices?.length > 0) {
        setData(res);
        setVisible(true);
      } else {
        setVisible(false);
      }
    } catch {
      setVisible(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // Also check periodically (every 30s) for new level-ups
  useEffect(() => {
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const handleConfirm = async () => {
    if (!selectedId) return;
    setConfirming(true);
    try {
      const res = await api.selectThemeChoice(selectedId);
      toast.success(`Unlocked: ${res.theme_name}!`);
      setTheme(selectedId);
      setVisible(false);
      setData(null);
      setSelectedId(null);
      // Check if more choices pending
      setTimeout(load, 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unlock theme");
    }
    setConfirming(false);
  };

  if (!visible || !data?.choices?.length) return null;

  const choice = data.choices[0];
  const optionA = choice.option_a;
  const optionB = choice.option_b;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)" }}
      data-testid="level-up-modal">
      <div className="max-w-lg w-full mx-4 space-y-5">
        {/* Header celebration */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center relative"
            style={{ background: "linear-gradient(135deg, #8B5CF620, #F59E0B20)", border: "1px solid #8B5CF630" }}>
            <Crown className="h-8 w-8" style={{ color: "#F59E0B" }} />
            <div className="absolute -top-2 -right-2">
              <Sparkles className="h-5 w-5 animate-pulse" style={{ color: "#8B5CF6" }} />
            </div>
          </div>
          <h2 className="text-2xl font-bold" style={{ color: "#F0F0F5" }} data-testid="level-up-title">
            Level {data.level}!
          </h2>
          <p className="text-sm" style={{ color: "#9898A8" }}>
            Choose a theme to unlock as your reward
          </p>
          <div className="flex items-center justify-center gap-1.5 py-1 px-3 rounded-full mx-auto w-fit"
            style={{ background: "#8B5CF615", border: "1px solid #8B5CF630" }}>
            <Star className="h-3 w-3" style={{ color: "#8B5CF6" }} />
            <span className="text-[11px] font-semibold" style={{ color: "#8B5CF6" }}>
              {data.pending_choices} choice{data.pending_choices > 1 ? "s" : ""} available
            </span>
          </div>
        </div>

        {/* Theme choices */}
        <div className={`grid ${optionB ? "grid-cols-2" : "grid-cols-1 max-w-xs mx-auto"} gap-3`}>
          {optionA && (
            <ThemePreviewCard
              theme={optionA}
              rarity={optionA.rarity}
              selected={selectedId === optionA.id}
              onSelect={() => setSelectedId(optionA.id)}
            />
          )}
          {optionB && (
            <ThemePreviewCard
              theme={optionB}
              rarity={optionB.rarity}
              selected={selectedId === optionB.id}
              onSelect={() => setSelectedId(optionB.id)}
            />
          )}
        </div>

        {/* Confirm button */}
        <div className="flex justify-center">
          <button
            disabled={!selectedId || confirming}
            onClick={handleConfirm}
            className="px-8 py-3 rounded-xl text-sm font-bold transition-all hover:scale-[1.02] disabled:opacity-40 disabled:scale-100"
            style={{
              background: selectedId ? "linear-gradient(135deg, #8B5CF6, #6366F1)" : "#1E1E2E",
              color: "#FFFFFF",
              boxShadow: selectedId ? "0 4px 20px #8B5CF640" : "none",
            }}
            data-testid="level-up-confirm">
            <span className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              {confirming ? "Unlocking..." : selectedId ? "Unlock This Theme" : "Select a Theme"}
            </span>
          </button>
        </div>

        {/* Skip option */}
        <div className="text-center">
          <button onClick={() => {
            // Bundle L PR-1: persist dismissal to localStorage per-session
            // so 30s polling + navigations don't re-open the modal.
            try { localStorage.setItem(DISMISS_KEY, getSessionId()); } catch { /* ignore */ }
            setVisible(false);
          }}
            className="text-[11px] transition-all hover:underline"
            style={{ color: "#52526B" }}
            data-testid="level-up-skip">
            Choose later
          </button>
        </div>
      </div>
    </div>
  );
}
